package uk.co.nickthecoder.kyd.nodes

import uk.co.nickthecoder.glok.property.BinaryFunction
import uk.co.nickthecoder.glok.property.Property
import uk.co.nickthecoder.glok.property.TernaryFunction
import uk.co.nickthecoder.glok.property.boilerplate.FloatUnaryFunction
import uk.co.nickthecoder.glok.property.boilerplate.floatProperty
import uk.co.nickthecoder.glok.property.functions.min
import uk.co.nickthecoder.glok.property.functions.times
import uk.co.nickthecoder.glok.property.property
import uk.co.nickthecoder.glok.scene.Node
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.kyd.Actor
import uk.co.nickthecoder.kyd.CoordinateSystem
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * Common features shared by [PlayActView] and [PlayStageView].
 * Deals with 3 coordinate systems [CoordinateSystem.world], [CoordinateSystem.pin]
 * and [CoordinateSystem.window].
 *
 * [sceneX], [sceneY], [width] and [height] are the basis for the conversion matrices
 * [sceneToIntermediate] and [intermediateToScene].
 *
 * [scale], [pan], [zoom] and [rotation] are the basis for the conversion matrices
 * [intermediateToWorld] and [worldToIntermediate].
 *
 * We combine pairs of matrices to form the conversion matrices [sceneToWorld] and [worldToScene].
 *
 * All of these matrices are backed by simple a [Property]<[Matrix]>, which are bound to
 * `property functions` (such as [BinaryFunction]). When any of the basic properties change (such as [panProperty]) change,
 * the matrices update automatically.
 * For maximum flexibility, you can unbind the matrix properties, and either update the matrices manually,
 * or set up different `property functions`.
 * For example, instead of world (0,0) mapping to the center of the view, you could make it
 * the near the center-bottom of the view.
 * In this case [sceneToIntermediate] would be changed from  :
 *
 *     Matrix(1f, 0f, 0f, - 1f).translate(- sceneX - width / 2, - sceneY - height / 2)
 *
 * to
 *
 *     Matrix(1f, 0f, 0f, - 1f).translate(- sceneX - width / 2, - sceneY - height * SOME_CONSTANT)
 */
abstract class StageOrActView : Node() {

    /**
     * The scaling factor to take account of this view not being the same as the
     * game's nominal size.
     *
     * NOTE. [zoomProperty] also affects the scaling.
     */
    val scaleProperty by floatProperty(1f)
    var scale by scaleProperty

    /**
     * The point in [CoordinateSystem.world] coordinates that corresponds to [viewPinRatioProperty].
     * This point will be the center of rotation and scaling.
     *
     * e.g. If this ere (0,0) and [viewPinRatio] were (0.5, 0.5), then an actor at (0,0) would appear in the
     * center of the view.
     * If we then changed this to (-100, 0), then the actor would shift to the right
     * (because (-100,0) is now at the center, so (0,0) would be further right).
     */
    val worldPinProperty by property<Vector2>(Vector2.ZERO)
    var worldPin by worldPinProperty

    /**
     * The point (as a ratio of the view's size from the top-left) that [worldPinProperty] corresponds to.
     * This point will be the center of rotation and scaling.
     *
     * e.g. If this were (0,0) and [worldPin] is (100, 200), then an actor at (100,200) would appear
     * at the top left of the view. If we then changed this to (0.5, 0.5) the actor would appear in the
     * center of the view.
     */
    val viewPinRatioProperty by property(Vector2(0.5f, 0.5f))
    var viewPinRatio by viewPinRatioProperty

    /**
     * An arbitrary zoom factor, which is unrelated to the size of this view.
     *
     * NOTE, [scaleProperty] also affect the scaling.
     */
    val zoomProperty by floatProperty(1f)
    var zoom by zoomProperty

    /**
     *
     */
    val rotationProperty by floatProperty(0f)
    var rotation by rotationProperty

    val combinedScaleProperty = scaleProperty * zoomProperty
    val combinedScale by combinedScaleProperty


    /**
     * A [Matrix] property which can convert [CoordinateSystem.world] coordinates (such as those used by [Actor.position])
     * to [CoordinateSystem.window] coordinates.
     *
     * This is a calculation based on [worldPinProperty], [viewPinRatio], [scaleProperty], [zoomProperty] and [rotationProperty],
     * as well as this view's [width] and [height] and its position [sceneX], [sceneY].
     *
     * [sceneToWorld] is the inverse of this matrix i.e.
     * `[worldToScene] * [sceneToWorld] == [Matrix.IDENTITY]`
     */
    val worldToSceneProperty by property<Matrix>(Matrix.IDENTITY)
    val worldToScene by worldToSceneProperty

    /**
     * A [Matrix] property which can convert [CoordinateSystem.window] coordinates to [CoordinateSystem.world] coordinates
     * (such as those used by [Actor.position]).
     *
     * This is the inverse of [worldToScene] i.e.
     * `[worldToScene] * [sceneToWorld] == [Matrix.IDENTITY]`
     */
    val sceneToWorldProperty by property<Matrix>(Matrix.IDENTITY)
    var sceneToWorld by sceneToWorldProperty

    /**
     * Because [sceneToWorld] and [worldToScene] have so many inputs, I split the job into two.
     * This is an intermediate step that you won't care about!?
     */
    val sceneToIntermediateProperty by property<Matrix>(Matrix.IDENTITY)
    var sceneToIntermediate by sceneToIntermediateProperty

    /**
     * Because [sceneToWorld] and [worldToScene] have so many inputs, I split the job into two.
     * This is an intermediate step that you won't care about!?
     */
    val intermediateToSceneProperty by property<Matrix>(Matrix.IDENTITY)
    var intermediateToScene by intermediateToSceneProperty

    /**
     * Because [sceneToWorld] and [worldToScene] have so many inputs, I split the job into two.
     * This is an intermediate step that you won't care about!?
     */
    val intermediateToWorldProperty by property<Matrix>(Matrix.IDENTITY)
    var intermediateToWorld by intermediateToWorldProperty

    /**
     * Because [sceneToWorld] and [worldToScene] have so many inputs, I split the job into two.
     * This is an intermediate step that you won't care about!?
     */
    val worldToIntermediateProperty by property<Matrix>(Matrix.IDENTITY)
    var worldToIntermediate by worldToIntermediateProperty

    init {

        sceneToIntermediateProperty.bindTo(
            TernaryFunction(sceneXProperty, sceneYProperty, viewPinRatioProperty) { sceneX, sceneY, ratio ->
                Matrix.scale(1f, - 1f)
                    .translate(- sceneX - width * ratio.x, - sceneY - height * ratio.y)
            }.apply {
                addDependent(heightProperty)
                addDependent(widthProperty)
                addDependent(heightProperty)
            })

        intermediateToSceneProperty.bindTo(
            TernaryFunction(sceneXProperty, sceneYProperty, viewPinRatioProperty) { sceneX, sceneY, ratio ->
                Matrix.translation(sceneX + width * ratio.x, sceneY + height * ratio.y)
                    .scale(1f, - 1f)
            }.apply {
                addDependent(widthProperty)
                addDependent(heightProperty)
            })

        worldToIntermediateProperty.bindTo(TernaryFunction(
            worldPinProperty, rotationProperty, combinedScaleProperty
        ) { worldCenter, rotation, scale ->
            Matrix.scale(scale)
                .rotate(-rotation)
                .translate(-worldCenter.x, - worldCenter.y)
        })

        intermediateToWorldProperty.bindTo(
            TernaryFunction(
                worldPinProperty, rotationProperty, combinedScaleProperty
            ) { worldCenter, rotation, scale ->
                Matrix.translation( worldCenter.x, worldCenter.y)
                .rotate(rotation)
                .scale(1 / scale)
            })

        worldToSceneProperty.bindTo(
            BinaryFunction(worldToIntermediateProperty, intermediateToSceneProperty) { a, b ->
                b * a
            })

        sceneToWorldProperty.bindTo(
            BinaryFunction(sceneToIntermediateProperty, intermediateToWorldProperty) { a, b ->
                b * a
            })

    }

    init {
        for (prop in listOf(
            scaleProperty, zoomProperty, rotationProperty, worldPinProperty, viewPinRatioProperty
        )) {
            prop.addListener(requestRedrawListener)
        }
    }

    /**
     * Ensures that [nominalSize] (in world coordinates) fits within the view.
     * If the view's aspect ratio is wider than [nominalSize]'s then more of the world will be visible at the edges.
     * On the other hand, if the view's aspect ratio is taller, then more of the world will be visible at the top
     * and bottom.
     *
     * This can be annoying, when you want things to appear in the corners of the view (such as a score or health etc.)
     * A simple way round this is to use a [Letterbox], which will place black bars at the top/bottom or left/right
     * as appropriate.
     * See `Constrain View` details of the `root` resource within the Kyd Editor.
     */
    fun scaleToNominalSize(nominalSize: Vector2) {
        scaleProperty.unbind()
        val xScale = FloatUnaryFunction(widthProperty) { width ->
            width / nominalSize.x
        }
        val yScale = FloatUnaryFunction(heightProperty) { height ->
            height / nominalSize.y
        }
        scaleProperty.bindTo(min(xScale, yScale))
    }
}
